import React, { useState } from "react";
import Joi from "joi-browser";
import Input from "../comman/input";
import Select from "../comman/select";
import TextArea from "../comman/textarea";
import PasswordInput from "../comman/PasswordInput";
import PhoneInput from "../comman/PhoneInput";

/**
 * useForm — functional-component replacement for the `Form` base class
 * (src/comman/form.jsx). Provides Joi-based validation plus the same
 * renderInput / renderSelect / renderTextArea helpers.
 *
 * Usage:
 *   const { data, errors, renderInput, validate, handleSubmit } =
 *     useForm(schema, initialData);
 *   ...
 *   <button disabled={Boolean(validate())} onClick={(e) => handleSubmit(e, doSubmit)}>
 */
const useForm = (schema = {}, initialData = {}) => {
  const [data, setData] = useState(initialData);
  const [errors, setErrors] = useState({});
  const [documents, setDocuments] = useState([]);

  const validate = () => {
    const options = { abortEarly: false, allowUnknown: true };
    const result = Joi.validate(data, schema, options);
    if (!result.error) return null;
    const validationErrors = {};
    for (let item of result.error.details) {
      if (item.type === "any.required") validationErrors[item.path[0]] = item.message;
    }
    return Object.keys(validationErrors).length > 0 ? validationErrors : null;
  };

  const validateProperty = ({ name, value }) => {
    if (schema[name] === undefined) return null;
    const obj = { [name]: value };
    const propSchema = { [name]: schema[name] };
    const { error } = Joi.validate(obj, propSchema);
    return error ? error.details[0].message : null;
  };

  const handleChange = ({ currentTarget: input }) => {
    const newErrors = { ...errors };
    const errorMessage = validateProperty(input);
    if (errorMessage) newErrors[input.name] = errorMessage;
    else delete newErrors[input.name];

    const newData = { ...data };
    newData[input.name] = input.value;

    // Additional validation for "paidAmount" (preserved from Form base class)
    if (input.name === "paidAmount") {
      const invoiceAmount = parseFloat(newData.invoiceAmount) || 0;
      const enteredAmount = parseFloat(input.value) || 0;
      if (enteredAmount > invoiceAmount) {
        newErrors[input.name] = "Amount to Pay cannot be greater than Invoice Amount!";
      } else if (enteredAmount === 0) {
        newErrors[input.name] = "Amount to Pay must be a number";
      } else {
        delete newErrors[input.name];
      }
    }

    setData(newData);
    setErrors(newErrors);
  };

  const handleSubmit = (e, doSubmit) => {
    if (e && e.preventDefault) e.preventDefault();
    const validationErrors = validate();
    setErrors(validationErrors || {});
    if (validationErrors) return;
    if (doSubmit) doSubmit();
  };

  const handleEnterKeyPress = (e, doSubmit) => {
    if (e.key === "Enter") handleSubmit(e, doSubmit);
  };

  const handleSavePhoneNumber = (phone, name) => {
    setData((prev) => ({ ...prev, [name]: phone }));
  };

  const renderInput = (name, label, type = "text", icon) => {
    if (type === "password") {
      return (
        <PasswordInput
          name={name}
          value={data[name] || ""}
          label={label}
          onChange={handleChange}
          error={errors[name]}
          req={schema[name]}
        />
      );
    } else if (type === "phone") {
      return (
        <PhoneInput
          name={name}
          value={data[name] || ""}
          label={label}
          onChange={handleChange}
          error={errors[name]}
          req={schema[name]}
        />
      );
    }
    return (
      <Input
        type={type}
        name={name}
        value={data[name] || ""}
        label={label}
        onChange={handleChange}
        error={errors[name]}
        req={schema[name]}
        icon={icon}
      />
    );
  };

  const renderEnterInput = (name, label, type = "text", doSubmit) => (
    <Input
      type={type}
      name={name}
      value={data[name] || ""}
      label={label}
      onChange={handleChange}
      error={errors[name]}
      req={schema[name]}
      onKeyDown={(e) => handleEnterKeyPress(e, doSubmit)}
    />
  );

  const renderTextArea = (name, label, icon) => (
    <TextArea
      name={name}
      value={data[name] || ""}
      label={label}
      onChange={handleChange}
      error={errors[name]}
      req={schema[name]}
      icon={icon}
    />
  );

  const renderSelect = (name, label, options, optName, optValue, icon) => (
    <Select
      name={name}
      value={data[name] || ""}
      label={label}
      options={options}
      onChange={handleChange}
      error={errors[name]}
      optName={optName}
      optValue={optValue}
      req={schema[name]}
      icon={icon}
    />
  );

  return {
    data,
    setData,
    errors,
    setErrors,
    documents,
    setDocuments,
    validate,
    validateProperty,
    handleChange,
    handleSubmit,
    handleEnterKeyPress,
    handleSavePhoneNumber,
    renderInput,
    renderEnterInput,
    renderTextArea,
    renderSelect,
  };
};

export default useForm;
